/**
 * Typed client for the shared server's `/api/auth/*` routes (PR-W2). Same
 * origin as the SPA (nginx proxies `/api` to the Node server in prod; Vite
 * proxies it in dev), so only a bearer token is needed for owner routes.
 */

import type { CwjProjectRuntimeContext } from "@smart-shikka/core/cwj-project-runtime";
import type { ProjectMetadataPublic } from "@smart-shikka/core/project-skill-registry";

export type AuthRole = "super-admin" | "super-sub-admin" | "user";
export type AccountStatus = "active" | "pending" | "blocked";

export interface PublicAccount {
  email: string;
  role: AuthRole;
  status: AccountStatus;
  createdAt: string;
  approvedAt: string | null;
  approvedBy: string | null;
  /**
   * Canonical membership onboarding flag (membership.onboarding_done). Absent
   * on older session payloads — consumers must treat only an explicit
   * `false` as "first run pending".
   */
  onboardingDone?: boolean;
}

export interface SessionInfo {
  /**
   * Present only for non-browser bearer clients. The web app logs in with
   * `sessionTransport: "cookie"`, where the server keeps the session in an
   * HttpOnly cookie and this field is absent by design.
   */
  token?: string;
  expiresAt: string;
}

/**
 * Stand-in passed to `api.*` calls by the browser app: the real session lives
 * in an HttpOnly cookie the page cannot read, so there is no bearer to hand
 * around. Seeing this value means "send the cookie, send no Authorization".
 */
export const COOKIE_SESSION_TOKEN = "__smart_shikka_cookie_session__";

const CSRF_COOKIE_NAME = "smart_shikka_csrf";
const CSRF_HEADER_NAME = "x-smart-shikka-csrf";

/** Read the non-HttpOnly CSRF cookie for the double-submit check. */
function readCsrfCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE_NAME) return decodeURIComponent(rest.join("="));
  }
  return undefined;
}

export interface MembershipRow {
  email: string;
  displayName: string | null;
  personName: string | null;
  company: string | null;
  age: number | null;
  mobile: string | null;
  whatsapp: string | null;
  altMobile: string | null;
  altEmail: string | null;
  address: string | null;
  district: string | null;
  state: string | null;
  country: string | null;
  pincode: string | null;
  trial: boolean;
  trialStarted: string | null;
  trialExpiry: string | null;
  licenseStatus: string;
  activeLicenses: number;
  onboardingDone: boolean;
  convertedDate: string | null;
  updatedAt: string | null;
}

export type CmsPageId = "terms" | "privacy" | "refund" | "aiPolicy" | "cookie" | "plans";

export interface CmsLocaleContent {
  title: string;
  subtitle: string;
  body: string;
}

export interface CmsPageRow {
  id: CmsPageId;
  en: CmsLocaleContent;
  hi: CmsLocaleContent;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type ModuleExecutionMode = "disabled" | "plan_only" | "approval_required" | "live";

export interface ModuleFlagRow {
  moduleId: string;
  mode: ModuleExecutionMode;
  enabled: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface AuditEventRow {
  domain: string;
  action: string;
  actor: string;
  actorRole: string;
  target: string | null;
  detail: string | null;
  outcome: string;
  denialCode: string | null;
  createdAt: string;
}

export type JarvisDesktopControl = "off" | "readonly";

export interface JarvisConfig {
  jarvisEnabled: boolean;
  emergencyStop: boolean;
  webAccess: boolean;
  publicSignup: boolean;
  newUserApproval: boolean;
  powershellExecuteDesktop: boolean;
  powershellExecuteWeb: boolean;
  terminalExecute: boolean;
  desktopControl: JarvisDesktopControl;
  browserControl: boolean;
  voice: boolean;
  providerRouter: boolean;
  realExecution: boolean;
  presentationMode: boolean;
  allowedEmails: string[];
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Boolean control keys the UI can toggle (excludes desktopControl/allowedEmails). */
export type JarvisToggleKey =
  | "jarvisEnabled"
  | "emergencyStop"
  | "webAccess"
  | "publicSignup"
  | "newUserApproval"
  | "powershellExecuteDesktop"
  | "powershellExecuteWeb"
  | "terminalExecute"
  | "browserControl"
  | "voice"
  | "providerRouter"
  | "realExecution"
  | "presentationMode";

export interface JarvisHelpSuggestion {
  shell: "powershell" | "bash";
  command: string;
  explanation: string;
  riskNote: string;
  risk: "low" | "medium" | "high";
  executeAllowed: false;
  actionExecuted: false;
}

export interface ControlJobRow {
  jobId: string;
  capabilityId: string;
  action: string;
  state: string;
  startedAt: number;
  remainingMs?: number;
  cancellable: boolean;
  waitingForApproval: boolean;
}

export interface ControlAuditRow {
  id: string;
  event: string;
  actor: string;
  agent: string;
  timestamp: number;
  summary: string;
  traceId: string;
  correlationId?: string | undefined;
}

export interface BrowserLeaseRow {
  leaseId: string;
  sessionId: string;
  controllerType: string;
  capabilityId: string;
  state: string;
  createdAt: number;
  expiresAt: number;
  heartbeatAt: number;
}

export interface BrowserArtifactRow {
  artifactId: string;
  path: string;
  filename: string;
  sha256: string;
  mimeType: string;
  size: number;
  createdAt: number;
  updatedAt: number;
  source: "screenshot" | "recording" | "download" | "upload" | "unknown";
  controlSessionId?: string | undefined;
  projectId?: string | undefined;
  pageUrl?: string | undefined;
  status: string;
}

export interface CodeControlLeaseRow {
  leaseId: string;
  projectId: string;
  action: string;
  worktreePath: string;
  baseCommit: string;
  createdAtMs: number;
  updatedAtMs: number;
  publishedCommit?: string | undefined;
}

export type MobileActionStatus =
  | "pending_confirmation"
  | "confirmed"
  | "denied"
  | "executing"
  | "completed"
  | "expired"
  | "failed";

export interface MobileActionResultRow {
  success: boolean;
  sanitizedOutput: string;
  auditId: string;
  executedAt: string;
}

export interface MobileActionRequestRow {
  requestId: string;
  deviceId: string;
  tenantId: string;
  ownerActorId: string;
  actionId: string;
  targetScope: string;
  payload: Record<string, unknown>;
  status: MobileActionStatus;
  createdAt: string;
  expiresAt: string;
  confirmedAt: string | null;
  result: MobileActionResultRow | null;
}

export interface ApiResult<T> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
  denialCode: string | null;
  failureKind?: ApiFailureKind | null;
  requestId?: string | null;
  retryAfterSeconds?: number | null;
}

export type ApiFailureKind = "network" | "proxy" | "unavailable" | "rate_limited" |
  "origin_denied" | "forbidden" | "unexpected_response" | "rejected";

function safeRequestId(value: string | null): string | null {
  if (!value || value.length > 64) return null;
  return /^(?:req_[a-zA-Z0-9_-]+|[a-fA-F0-9]{32}|[a-fA-F0-9]{8}(?:-[a-fA-F0-9]{4}){3}-[a-fA-F0-9]{12})$/.test(value) ? value : null;
}

function retryAfterSeconds(value: string | null): number | null {
  if (!value) return null;
  const seconds = /^\d{1,5}$/.test(value) ? Number(value) :
    /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/.test(value)
      ? Math.ceil((Date.parse(value) - Date.now()) / 1000) : NaN;
  return Number.isFinite(seconds) && seconds >= 0 && seconds <= 86400 ? seconds : null;
}

function responseFailure(status: number, payload: Record<string, unknown> | null): ApiFailureKind {
  if (status === 429) return "rate_limited";
  if (status === 404 || status === 502) return "proxy";
  if (status >= 500) return "unavailable";
  if (status === 403) return payload?.errorCode === "ORIGIN_NOT_ALLOWED" ? "origin_denied" : "forbidden";
  if (!payload) return "unexpected_response";
  return "rejected";
}

function isLoginSession(payload: Record<string, unknown>): boolean {
  const account = payload.account;
  const session = payload.session;
  return !!account && typeof account === "object" && !Array.isArray(account) &&
    "email" in account && typeof account.email === "string" &&
    "role" in account && typeof account.role === "string" &&
    "status" in account && account.status === "active" &&
    !!session && typeof session === "object" && !Array.isArray(session) &&
    "expiresAt" in session && typeof session.expiresAt === "string" && !!session.expiresAt;
}

const API_BASE = "/api";

let deviceKeyPair: CryptoKeyPair | null = null;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return typeof btoa === "function" ? btoa(binary) : binary;
}

function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function sha256Hex(input: string): Promise<string> {
  if (typeof crypto === "undefined" || !crypto.subtle) return "";
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return arrayBufferToHex(digest);
}

function isMobileDevicePath(path: string): boolean {
  return path.startsWith("/mobile/") && !path.startsWith("/mobile-pairing/");
}

async function signMobileRequest(
  path: string,
  method: "GET" | "POST",
  body: unknown,
  keyPair: CryptoKeyPair,
): Promise<Record<string, string> | undefined> {
  if (typeof crypto === "undefined" || !crypto.subtle) return undefined;
  try {
    const timestamp = String(Date.now());
    const nonce = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const bodyHash = await sha256Hex(body === undefined ? "" : JSON.stringify(body));
    const data = `${timestamp}:${nonce}:${method}:${path}:${bodyHash}`;
    const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, keyPair.privateKey, new TextEncoder().encode(data));
    return {
      "x-device-timestamp": timestamp,
      "x-device-nonce": nonce,
      "x-device-signature": arrayBufferToBase64(signature),
    };
  } catch {
    return undefined;
  }
}

async function request<T>(
  path: string,
  method: "GET" | "POST",
  token: string | null,
  body?: unknown,
  signal?: AbortSignal,
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  const cookieSession = token === COOKIE_SESSION_TOKEN;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token && !cookieSession) headers.Authorization = `Bearer ${token}`;
  if (method === "POST") {
    const csrf = readCsrfCookie();
    if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  }
  if (token && !cookieSession && deviceKeyPair && isMobileDevicePath(path)) {
    const signed = await signMobileRequest(path, method, body, deviceKeyPair);
    if (signed) Object.assign(headers, signed);
  }
  // Same-origin cookie session: the browser never holds a readable bearer.
  const loginRequest = path === "/auth/login";
  const controller = loginRequest && !signal ? new AbortController() : null;
  const timeout = controller ? setTimeout(() => controller.abort(), 15000) : null;
  const init: RequestInit = { method, headers, credentials: "include", signal: signal ?? controller?.signal ?? null };
  if (body !== undefined) init.body = JSON.stringify(body);
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, init);
  } catch {
    if (timeout) clearTimeout(timeout);
    return {
      ok: false, status: 0, data: null, error: "network_error", denialCode: null,
      failureKind: "network", requestId: null, retryAfterSeconds: null,
    };
  }
  let payload: Record<string, unknown> | null = null;
  try {
    const parsed: unknown = await response.json();
    if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
      payload = parsed as Record<string, unknown>;
    }
  } catch {
    /* A proxy page or malformed body is not an API success. Never return its text. */
  } finally {
    if (timeout) clearTimeout(timeout);
  }
  const malformedLogin = loginRequest && response.ok && payload !== null &&
    payload.ok !== false && payload.success !== false && !isLoginSession(payload);
  const ok = response.ok && payload !== null && payload.ok !== false && payload.success !== false && !malformedLogin;
  const failureKind = malformedLogin ? "unexpected_response" : ok ? null : responseFailure(response.status, payload);
  const error = loginRequest && !ok
    ? failureKind === "unavailable" ? "service_unavailable" : failureKind
    : typeof payload?.error === "string" ? payload.error : null;
  return {
    ok,
    status: response.status,
    data: payload as T | null,
    error,
    denialCode: typeof payload?.denialCode === "string" ? payload.denialCode : null,
    failureKind,
    requestId: safeRequestId(response.headers?.get("x-smart-shikka-request-id") ?? response.headers?.get("x-request-id") ?? null),
    retryAfterSeconds: retryAfterSeconds(response.headers?.get("Retry-After") ?? null),
  };
}

export interface AuthSuccess {
  ok: boolean;
  session: SessionInfo | null;
  account: PublicAccount | null;
  denialCode: string | null;
  /** True when a privileged account must supply an emailed 2FA code. */
  twoFactorRequired?: boolean;
}

export const api = {
  signup: (email: string, password: string) =>
    request<AuthSuccess>("/auth/signup", "POST", null, { email, password, sessionTransport: "cookie" }),
  /**
   * Login. Pass `code` to complete the 2FA step for privileged accounts.
   * `sessionTransport: "cookie"` asks the server to keep the session in an
   * HttpOnly cookie instead of returning a raw bearer to page JavaScript.
   */
  login: (email: string, password: string, code?: string) =>
    request<AuthSuccess>("/auth/login", "POST", null, {
      email,
      password,
      sessionTransport: "cookie",
      ...(code ? { code } : {}),
    }),
  session: (token: string) =>
    request<{ ok: boolean; account: PublicAccount; role: AuthRole }>("/auth/session", "GET", token),
  logout: (token: string) => request<{ ok: boolean }>("/auth/logout", "POST", token, {}),
  /** Marks the canonical membership onboarding flag for the signed-in account. */
  completeOnboarding: (token: string) =>
    request<{ ok: boolean }>("/auth/onboarding-complete", "POST", token, {}),
  // The OTP request routes answer identically for existing and unknown
  // accounts, so the response carries no delivery/account signal.
  requestVerify: (email: string, channel: "email" | "whatsapp") =>
    request<{ ok: boolean; channel: string }>("/auth/request-verify", "POST", null, { email, channel }),
  verify: (email: string, code: string) =>
    request<{ ok: boolean }>("/auth/verify", "POST", null, { email, code }),
  requestReset: (email: string, channel: "email" | "whatsapp") =>
    request<{ ok: boolean; channel: string }>("/auth/request-reset", "POST", null, { email, channel }),
  reset: (email: string, code: string, newPassword: string) =>
    request<{ ok: boolean }>("/auth/reset", "POST", null, { email, code, newPassword }),

  // public CMS reads
  cmsPages: () => request<{ ok: boolean; pages: CmsPageRow[] }>("/auth/cms/pages", "GET", null),
  cmsPage: (slug: string) => request<{ ok: boolean; page: CmsPageRow }>(`/auth/cms/pages/${slug}`, "GET", null),

  // owner-only panels
  adminAccounts: (token: string) => request<{ ok: boolean; accounts: PublicAccount[] }>("/auth/admin/accounts", "GET", token),
  adminSetRole: (token: string, email: string, role: "user" | "super-sub-admin") =>
    request<{ ok: boolean; account: PublicAccount }>("/auth/admin/set-role", "POST", token, { email, role }),
  adminMemberships: (token: string) => request<{ ok: boolean; memberships: MembershipRow[] }>("/auth/admin/memberships", "GET", token),
  adminSaveMembership: (token: string, membership: MembershipRow) =>
    request<{ ok: boolean }>("/auth/admin/memberships", "POST", token, { membership }),
  adminModuleFlags: (token: string) => request<{ ok: boolean; flags: ModuleFlagRow[] }>("/auth/admin/module-flags", "GET", token),
  adminSetModuleFlag: (token: string, moduleId: string, mode: ModuleExecutionMode) =>
    request<{ ok: boolean }>("/auth/admin/module-flags", "POST", token, { moduleId, mode }),
  adminSaveCmsPage: (token: string, page: Pick<CmsPageRow, "id" | "en" | "hi">) =>
    request<{ ok: boolean }>("/auth/admin/cms/pages", "POST", token, page),
  adminAudit: (token: string) => request<{ ok: boolean; events: AuditEventRow[] }>("/auth/admin/audit", "GET", token),

  // Shikka CEO Control Center (allowlist-gated; 403 => hide the panel)
  jarvisConfig: (token: string) =>
    request<{ ok: boolean; config: JarvisConfig; isJarvisAdmin: boolean }>("/auth/admin/jarvis/config", "GET", token),
  jarvisSetConfig: (token: string, patch: Partial<Record<JarvisToggleKey, boolean>> & { desktopControl?: JarvisDesktopControl; allowedEmails?: string[] }) =>
    request<{ ok: boolean; config: JarvisConfig }>("/auth/admin/jarvis/config", "POST", token, { patch }),
  jarvisEmergencyStop: (token: string) =>
    request<{ ok: boolean; config: JarvisConfig }>("/auth/admin/jarvis/emergency-stop", "POST", token, {}),
  jarvisHelp: (token: string, prompt: string, shell: "powershell" | "bash") =>
    request<{ ok: boolean; suggestion: JarvisHelpSuggestion }>("/auth/admin/jarvis/help", "POST", token, { prompt, shell }),
  controlJobs: (token: string, projectId: string) =>
    request<{ success: boolean; jobs: ControlJobRow[] }>("/control/jobs", "POST", token, { projectId }),
  controlAuditTimeline: (token: string, projectId: string) =>
    request<{ success: boolean; events: ControlAuditRow[] }>("/control/audit-timeline", "POST", token, { projectId }),
  browserSessionLeases: (token: string, projectId: string) =>
    request<{ success: boolean; leases: BrowserLeaseRow[] }>("/browser-session/lease", "POST", token, {
      operation: "list",
      projectId,
      controllerType: "web",
    }),
  codeControlLeases: (token: string, projectId: string) =>
    request<{ success: boolean; leases: CodeControlLeaseRow[] }>(`/projects/${encodeURIComponent(projectId)}/code-control/leases`, "GET", token),
  browserArtifacts: (token: string) =>
    request<{ success: boolean; artifacts: BrowserArtifactRow[] }>("/control/browser/artifacts", "GET", token),

  // Mobile Pairing — action sync (owner-only)
  mobilePairingRequest: (token: string, deviceName: string) =>
    request<{ ok: boolean; requestId: string; qrUrl: string; oneTimeCode: string; expiresAt: string }>("/mobile-pairing/request", "POST", token, { deviceName }),
  mobilePairingStatus: (token: string) =>
    request<{ ok: boolean; emergencyStop: boolean; activeDevices: number; pendingActions: number }>("/mobile-pairing/status", "GET", token),
  mobilePairingPending: (token: string) =>
    request<{ ok: boolean; actions: MobileActionRequestRow[] }>("/mobile-pairing/pending-actions", "GET", token),
  mobilePairingConfirm: (token: string, requestId: string, confirmed: boolean, deviceId?: string) =>
    request<{ ok: boolean; status: string }>("/mobile-pairing/confirm-action", "POST", token, { requestId, confirmed, deviceId }),
  mobilePairingResult: (token: string, requestId: string) =>
    request<{ ok: boolean; status: string; result: MobileActionResultRow | null }>(`/mobile-pairing/action-result?requestId=${encodeURIComponent(requestId)}`, "GET", token),
  mobilePairingComplete: (token: string, requestId: string, oneTimeCode: string, devicePublicKey?: string) =>
    request<{ ok: boolean; device: { deviceId: string; deviceName: string; status: string; pairedAt: string; expiresAt: string }; sessionToken: string; sessionTokenExpiresAt: string }>("/mobile-pairing/complete", "POST", token, { requestId, oneTimeCode, devicePublicKey }),

  // Mobile Client — device- and owner-facing secure PWA surface
  mobileHealth: () =>
    request<{ ok: boolean; apiVersion: string; minDesktopVersion: string; minMobileVersion: string; featureFlags: string[]; maintenance: boolean; emergencyStop: boolean }>("/mobile/health", "GET", null),
  mobileHeartbeat: (token: string) =>
    request<{ ok: boolean; deviceId: string; lastSeenAt: string }>("/mobile/heartbeat", "POST", token, {}),
  mobileAction: (token: string, actionId: string, targetScope: string, payload?: Record<string, unknown>) =>
    request<{ ok: boolean; requestId: string; status: string; expiresAt: string }>("/mobile/action", "POST", token, { actionId, targetScope, payload }),
  mobileActionResult: (token: string, requestId: string) =>
    request<{ ok: boolean; requestId: string; status: string; result: MobileActionResultRow | null; expiresAt: string }>(`/mobile/action-result?requestId=${encodeURIComponent(requestId)}`, "GET", token),
  mobileChat: (token: string, profile: "fast-assistant" | "core-reasoning", message: string) =>
    request<{ ok: boolean; reply: string; profile: string; model: string; providerId: string }>("/mobile/chat", "POST", token, { profile, message }),
  mobileDevices: (token: string) =>
    request<{ ok: boolean; devices: unknown[] }>("/mobile/devices", "GET", token),
  mobileRevoke: (token: string, deviceId: string) =>
    request<{ ok: boolean; deviceId: string; status: string }>("/mobile/revoke", "POST", token, { deviceId }),
  mobileRevokeAll: (token: string) =>
    request<{ ok: boolean; revokedCount: number }>("/mobile/revoke-all", "POST", token, {}),
  mobileDevice: (token: string, deviceId?: string) =>
    request<{ ok: boolean; deviceId: string; deviceName: string; status: string; pairedAt: string; lastSeenAt: string | null; expiresAt: string; revokedAt?: string | null }>(`/mobile/device${deviceId ? `?deviceId=${encodeURIComponent(deviceId)}` : ""}`, "GET", token),
  mobileDesktopStatus: (token: string) =>
    request<{ ok: boolean; bindings: unknown[] }>("/mobile/desktop/status", "GET", token),
  mobileDesktopHandoff: (token: string, context: "code") =>
    request<{ ok: boolean; handoffId: string; deepLink: string; expiresAt: string }>("/mobile/desktop/handoff", "POST", token, { context }),

  desktopRelease: () =>
    request<{ version: string; installerUrl: string; installerSha256?: string | null; releaseNotesUrl?: string | null; signingRequired?: boolean; signatureVerified?: boolean; signer?: string }>("/desktop/release", "GET", null),
  publicDesktopRelease: () =>
    request<{ version: string; installerUrl: string; installerSha256?: string | null; releaseNotesUrl?: string | null; signingRequired?: boolean; signatureVerified?: boolean; signer?: string }>("/public/desktop/release", "GET", null),
  publicBrowserExtensionRelease: () =>
    request<{ version: string; packageUrl: string; sha256: string; releaseNotesUrl: string; installMode: "private_unpacked" | "store" }>("/public/browser-extension/release", "GET", null),

  // CWJ-04 — project runtime (server-authoritative)
  projects: (token: string, signal?: AbortSignal) =>
    request<{ success: boolean; projects: ProjectMetadataPublic[]; count: number }>("/projects", "GET", token, undefined, signal),
  project: (token: string, projectId: string, signal?: AbortSignal) =>
    request<{ success: boolean; project: ProjectMetadataPublic }>(`/projects/${encodeURIComponent(projectId)}`, "GET", token, undefined, signal),
  projectSwitch: (token: string, projectId: string, skillId?: string, signal?: AbortSignal) =>
    request<{ success: boolean; context: CwjProjectRuntimeContext }>("/projects/switch", "POST", token, { projectId, skillId }, signal),

  // PR-21 — unified command center mission control
  // Device key provisioning for proof-of-possession. Returns the base64 SPKI public key.
  provisionDeviceKeyPair: async (): Promise<string | null> => {
    if (typeof crypto === "undefined" || !crypto.subtle) return null;
    try {
      const keyPair = await crypto.subtle.generateKey(
        { name: "ECDSA", namedCurve: "P-256" },
        false,
        ["sign", "verify"],
      );
      deviceKeyPair = keyPair;
      const spki = await crypto.subtle.exportKey("spki", keyPair.publicKey);
      return arrayBufferToBase64(spki);
    } catch {
      return null;
    }
  },
  clearDeviceKeyPair: () => { deviceKeyPair = null; },

  // PR-21 — unified command center mission control
  createMission: (token: string, input: { title: string; description?: string; agentCount: number; requiresApproval?: boolean }) =>
    request<{ success: boolean; runId: string; status: string; agentCount: number; stageCount: number }>("/cowork/missions", "POST", token, input),
  listMissions: (token: string) =>
    request<{ success: boolean; missions: unknown[]; count: number }>("/cowork/missions", "GET", token),
  pauseMission: (token: string, runId: string, reason?: string) =>
    request<{ success: boolean; runId: string; status: string }>(`/cowork/runs/${encodeURIComponent(runId)}/pause`, "POST", token, { reason: reason ?? "paused by user" }),
  resumeMission: (token: string, runId: string) =>
    request<{ success: boolean; runId: string; status: string }>(`/cowork/runs/${encodeURIComponent(runId)}/resume`, "POST", token, {}),
  cancelMission: (token: string, runId: string, reason?: string) =>
    request<{ success: boolean; runId: string; status: string }>(`/cowork/runs/${encodeURIComponent(runId)}/cancel`, "POST", token, { reason: reason ?? "cancelled by user" }),
};

// ---- CORE-HELP-02: canonical Help Desk (Core is the single support authority) ----

export type SupportTicketStatus =
  | "open" | "human_needed" | "in_progress" | "waiting_customer" | "resolved" | "closed";

export interface SupportTicket {
  ticketId: string;
  ticketNumber: string;
  subject: string;
  category: string;
  priority: string;
  status: SupportTicketStatus;
  sourceApp: string;
  requesterActorId: string;
  assignedActorId?: string | undefined;
  aiDisposition?: string | undefined;
  escalationReason?: string | undefined;
  waitingSince?: number | undefined;
  resolvedAt?: number | undefined;
  closedAt?: number | undefined;
  reopenedCount: number;
  firstResponseDueAt?: number | undefined;
  resolutionDueAt?: number | undefined;
  createdAt: number;
  updatedAt: number;
}

export interface SupportMessage {
  messageId: string;
  ticketId: string;
  authorActorId: string;
  authorRole: string;
  kind: "customer_message" | "human_reply" | "internal_note" | "system_event";
  body: string;
  createdAt: number;
}

export interface SupportEvent {
  eventId: string;
  eventType: string;
  actorId: string;
  metadata?: Record<string, unknown> | undefined;
  createdAt: number;
}

export interface SupportAttachment {
  attachmentId: string;
  ticketId: string;
  messageId?: string | undefined;
  artifactId: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  sha256: string;
  createdAt: number;
}

export interface SupportNotification {
  notificationId: string;
  ticketId: string;
  eventType: string;
  channel: string;
  status: string;
  payload?: Record<string, unknown> | undefined;
  readAt?: number | undefined;
  deepLink?: string | undefined;
  createdAt: number;
}

/** Server-authoritative support access truth — the UI gates every support
 * surface off this, never the login role (merge-gate correction). */
export interface SupportAccessTruth {
  capability: "user" | "staff" | "manager";
  canUseInbox: boolean;
  canManageSla: boolean;
}

export interface SupportChannelTruth {
  channel: "in_app" | "email" | "whatsapp" | "telegram";
  status: "ready" | "blocked_config" | "adapter_ready_provider_not_configured" | "post_beta_optional";
  providerConfigured: boolean;
}

export interface SupportTicketDetail {
  ticket: SupportTicket;
  messages: SupportMessage[];
  attachments: SupportAttachment[];
  events: SupportEvent[];
}

export interface SupportSlaTruth {
  configured: boolean;
  status?: string | undefined;
  policies: { priority: string; firstResponseMs: number; resolutionMs: number }[];
}

const supportUnwrap = async <T>(p: Promise<ApiResult<T>>): Promise<T> => {
  const r = await p;
  if (!r.ok || r.data === null) {
    throw new Error(r.error ?? r.denialCode ?? `request failed (${r.status})`);
  }
  return r.data;
};

const supportGet = <T>(token: string, path: string) => supportUnwrap(request<T>(`/support${path}`, "GET", token));
const supportPost = <T>(token: string, path: string, body: unknown) => supportUnwrap(request<T>(`/support${path}`, "POST", token, body));

export const supportApi = {
  access: (token: string) =>
    supportGet<{ success: boolean; access: SupportAccessTruth }>(token, "/access"),
  listTickets: (token: string) =>
    supportGet<{ success: boolean; tickets: SupportTicket[] }>(token, "/tickets"),
  createTicket: (token: string, input: { subject: string; body: string; category?: string; priority?: string; explicitHumanRequest?: boolean; sourceConversationId?: string; context?: Record<string, unknown>; idempotencyKey?: string }) =>
    supportPost<{ success: boolean; ticket: SupportTicket; created: boolean }>(token, "/tickets", input),
  ticketDetail: (token: string, ticketId: string) =>
    supportGet<{ success: boolean } & SupportTicketDetail>(token, `/tickets/${encodeURIComponent(ticketId)}`),
  reply: (token: string, ticketId: string, body: string) =>
    supportPost<{ success: boolean; message: SupportMessage }>(token, `/tickets/${encodeURIComponent(ticketId)}/messages`, { body }),
  escalate: (token: string, ticketId: string, reason?: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/human-escalation`, reason ? { reason } : {}),
  resolve: (token: string, ticketId: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/resolve`, {}),
  feedback: (token: string, ticketId: string, solved: boolean) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/feedback`, { solved }),
  reopen: (token: string, ticketId: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/reopen`, {}),
  notifications: (token: string) =>
    supportGet<{ success: boolean; notifications: SupportNotification[]; unread: number; channels: SupportChannelTruth[] }>(token, "/notifications"),
  markNotificationsRead: (token: string, notificationIds?: string[]) =>
    supportPost<{ success: boolean; marked: number; unreadCount: number }>(token, "/notifications/mark-read", notificationIds ? { notificationIds } : {}),

  // Staff
  inbox: (token: string, view: string, filters?: { category?: string; priority?: string; sourceApp?: string }) => {
    const params = new URLSearchParams({ view });
    if (filters?.category) params.set("category", filters.category);
    if (filters?.priority) params.set("priority", filters.priority);
    if (filters?.sourceApp) params.set("sourceApp", filters.sourceApp);
    return supportGet<{ success: boolean; tickets: SupportTicket[]; count: number }>(token, `/inbox?${params.toString()}`);
  },
  assign: (token: string, ticketId: string, assigneeActorId?: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/assign`, assigneeActorId ? { assigneeActorId } : {}),
  internalNote: (token: string, ticketId: string, body: string) =>
    supportPost<{ success: boolean; message: SupportMessage }>(token, `/tickets/${encodeURIComponent(ticketId)}/internal-note`, { body }),
  changeCategory: (token: string, ticketId: string, category: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/category`, { category }),
  changePriority: (token: string, ticketId: string, priority: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/priority`, { priority }),
  attachFile: (token: string, ticketId: string, input: { filename: string; mime: string; contentBase64: string; messageId?: string }) =>
    supportPost<{ success: boolean; attachment: SupportAttachment }>(token, `/tickets/${encodeURIComponent(ticketId)}/attachments`, input),
  /** Ticket-authorized download — decrypted bytes come only through this
   * authenticated route; there is no public artifact URL. */
  downloadAttachment: (token: string, ticketId: string, attachmentId: string) =>
    supportGet<{ success: boolean; attachment: SupportAttachment; contentBase64: string }>(token, `/tickets/${encodeURIComponent(ticketId)}/attachments/${encodeURIComponent(attachmentId)}`),
  close: (token: string, ticketId: string) =>
    supportPost<{ success: boolean; ticket: SupportTicket }>(token, `/tickets/${encodeURIComponent(ticketId)}/close`, {}),
  sla: (token: string) =>
    supportGet<{ success: boolean; sla: SupportSlaTruth }>(token, "/sla"),
  upsertSla: (token: string, policy: { priority: string; firstResponseMs: number; resolutionMs: number }) =>
    supportPost<{ success: boolean }>(token, "/sla", policy),
  processNotifications: (token: string) =>
    supportPost<{ success: boolean; processed: number; delivered: number; retried: number; failed: number; channels: SupportChannelTruth[] }>(token, "/notifications/process", {}),
};
